import { supabase } from '../lib/supabase';

// ─── Document Annotations ─────────────────────────────────────────────────────

export const getAnnotationsByDocument = async (documentId) => {
  try {
    const { data, error } = await supabase
      ?.from('notaire_document_annotations')
      ?.select('*, notary:notaries(name)')
      ?.eq('document_id', documentId)
      ?.order('created_at', { ascending: true });
    if (error) return { data: [], error: error?.message };
    return { data: data || [], error: null };
  } catch (err) {
    return { data: [], error: 'Impossible de charger les annotations.' };
  }
};

export const addAnnotation = async (documentId, notaryId, annotationText, annotationType = 'comment') => {
  try {
    const { data, error } = await supabase
      ?.from('notaire_document_annotations')
      ?.insert([{ document_id: documentId, notary_id: notaryId, annotation_text: annotationText, annotation_type: annotationType }])
      ?.select()
      ?.single();
    if (error) return { data: null, error: error?.message };
    return { data, error: null };
  } catch (err) {
    return { data: null, error: 'Impossible d\'ajouter l\'annotation.' };
  }
};

export const resolveAnnotation = async (annotationId) => {
  try {
    const { data, error } = await supabase
      ?.from('notaire_document_annotations')
      ?.update({ is_resolved: true, resolved_at: new Date()?.toISOString() })
      ?.eq('id', annotationId)
      ?.select()
      ?.single();
    if (error) return { data: null, error: error?.message };
    return { data, error: null };
  } catch (err) {
    return { data: null, error: 'Impossible de résoudre l\'annotation.' };
  }
};

export const deleteAnnotation = async (annotationId) => {
  try {
    const { error } = await supabase
      ?.from('notaire_document_annotations')
      ?.delete()
      ?.eq('id', annotationId);
    if (error) return { error: error?.message };
    return { error: null };
  } catch (err) {
    return { error: 'Impossible de supprimer l\'annotation.' };
  }
};

// ─── Consultations Calendar ───────────────────────────────────────────────────

export const getConsultationsByNotary = async (notaryId) => {
  try {
    const { data, error } = await supabase
      ?.from('notaire_consultations')
      ?.select('*, dossier:notaire_dossiers(reference, property_title)')
      ?.eq('notary_id', notaryId)
      ?.order('consultation_date', { ascending: true });
    if (error) return { data: [], error: error?.message };
    return { data: data || [], error: null };
  } catch (err) {
    return { data: [], error: 'Impossible de charger les consultations.' };
  }
};

export const createConsultation = async (consultationData) => {
  try {
    const { data, error } = await supabase
      ?.from('notaire_consultations')
      ?.insert([consultationData])
      ?.select()
      ?.single();
    if (error) return { data: null, error: error?.message };
    return { data, error: null };
  } catch (err) {
    return { data: null, error: 'Impossible de créer la consultation.' };
  }
};

export const updateConsultationStatus = async (consultationId, status) => {
  try {
    const { data, error } = await supabase
      ?.from('notaire_consultations')
      ?.update({ status })
      ?.eq('id', consultationId)
      ?.select()
      ?.single();
    if (error) return { data: null, error: error?.message };
    return { data, error: null };
  } catch (err) {
    return { data: null, error: 'Impossible de mettre à jour la consultation.' };
  }
};

// ─── Transaction Timeline ─────────────────────────────────────────────────────

export const getTimelineByDossier = async (dossierId) => {
  try {
    const { data, error } = await supabase
      ?.from('transaction_timeline_events')
      ?.select('*, triggered_by_profile:user_profiles!transaction_timeline_events_triggered_by_fkey(full_name)')
      ?.eq('dossier_id', dossierId)
      ?.order('created_at', { ascending: false });
    if (error) return { data: [], error: error?.message };
    return { data: data || [], error: null };
  } catch (err) {
    return { data: [], error: 'Impossible de charger la timeline.' };
  }
};

export const addTimelineEvent = async (dossierId, eventType, eventTitle, eventDescription = '', triggeredByRole = 'notaire', metadata = {}) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase
      ?.from('transaction_timeline_events')
      ?.insert([{
        dossier_id: dossierId,
        event_type: eventType,
        event_title: eventTitle,
        event_description: eventDescription,
        triggered_by: user?.id || null,
        triggered_by_role: triggeredByRole,
        metadata,
      }])
      ?.select()
      ?.single();
    if (error) return { data: null, error: error?.message };
    return { data, error: null };
  } catch (err) {
    return { data: null, error: 'Impossible d\'ajouter l\'événement.' };
  }
};

// ─── Notary Assignment ────────────────────────────────────────────────────────

export const getAllNotariesWithWorkload = async () => {
  try {
    const { data, error } = await supabase
      ?.from('notaries')
      ?.select(`
        *,
        workload:notaire_workload(active_dossiers_count, max_capacity, availability_status)
      `)
      ?.eq('is_verified', true)
      ?.order('name');
    if (error) return { data: [], error: error?.message };
    return { data: data || [], error: null };
  } catch (err) {
    return { data: [], error: 'Impossible de charger les notaires.' };
  }
};

export const getUnassignedDossiers = async () => {
  try {
    const { data, error } = await supabase
      ?.from('notaire_dossiers')
      ?.select('*')
      ?.is('notary_id', null)
      ?.order('created_at', { ascending: false });
    if (error) return { data: [], error: error?.message };
    return { data: data || [], error: null };
  } catch (err) {
    return { data: [], error: 'Impossible de charger les dossiers non assignés.' };
  }
};

export const assignDossierToNotary = async (dossierId, notaryId, assignedBy, notes = '', priority = 'normal') => {
  try {
    // Update dossier notary_id
    const { error: dossierError } = await supabase
      ?.from('notaire_dossiers')
      ?.update({ notary_id: notaryId })
      ?.eq('id', dossierId);
    if (dossierError) return { error: dossierError?.message };

    // Upsert assignment record
    const { data, error } = await supabase
      ?.from('notaire_assignments')
      ?.upsert([{
        dossier_id: dossierId,
        notary_id: notaryId,
        assigned_by: assignedBy,
        assignment_notes: notes,
        priority,
        assigned_at: new Date()?.toISOString(),
        status: 'pending',
      }], { onConflict: 'dossier_id' })
      ?.select()
      ?.single();
    if (error) return { data: null, error: error?.message };

    // Add timeline event
    await addTimelineEvent(dossierId, 'status_change', 'Dossier assigné à un notaire', 'Un notaire a été assigné à ce dossier.', 'admin', { notary_id: notaryId });

    return { data, error: null };
  } catch (err) {
    return { data: null, error: 'Impossible d\'assigner le dossier.' };
  }
};

export const batchAssignDossiers = async (assignments, assignedBy) => {
  const results = await Promise.all(
    assignments?.map(({ dossierId, notaryId, priority }) =>
      assignDossierToNotary(dossierId, notaryId, assignedBy, '', priority)
    )
  );
  const errors = results?.filter(r => r?.error);
  return { success: results?.length - errors?.length, errors: errors?.length };
};

export const updateNotaryWorkload = async (notaryId, maxCapacity, availabilityStatus) => {
  try {
    const { data, error } = await supabase
      ?.from('notaire_workload')
      ?.upsert([{ notary_id: notaryId, max_capacity: maxCapacity, availability_status: availabilityStatus, last_updated: new Date()?.toISOString() }], { onConflict: 'notary_id' })
      ?.select()
      ?.single();
    if (error) return { data: null, error: error?.message };
    return { data, error: null };
  } catch (err) {
    return { data: null, error: 'Impossible de mettre à jour la charge.' };
  }
};

// ─── Pending Actions for Notary Workspace ────────────────────────────────────

export const getNotaryPendingActions = async (notaryId) => {
  try {
    const [dossiersRes, docsRes] = await Promise.all([
      supabase?.from('notaire_dossiers')?.select('id, reference, property_title, status, created_at')
        ?.eq('notary_id', notaryId)?.eq('status', 'nouveau')?.order('created_at', { ascending: false }),
      supabase?.from('notaire_documents')
        ?.select('id, document_name, document_type, status, created_at, dossier:notaire_dossiers!inner(id, reference, notary_id)')
        ?.eq('notaire_dossiers.notary_id', notaryId)?.eq('status', 'en_attente')?.order('created_at', { ascending: false })?.limit(20),
    ]);
    return {
      newDossiers: dossiersRes?.data || [],
      pendingDocuments: docsRes?.data || [],
    };
  } catch (err) {
    return { newDossiers: [], pendingDocuments: [] };
  }
};
